import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useGlobalState } from '../contexts/GlobalContext';
import { apiFetch } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';
import Snackbar from './Snackbar';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

function PropertyDetail() {
  const { id } = useParams();
  const location = useLocation();
  const { state, dispatch } = useGlobalState();
  const [property, setProperty] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [status, setStatus] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [saleDate, setSaleDate] = useState('');
  const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const navigate = useNavigate();
  const stellarListing = location.state?.stellarListing || null;
  const isExternalStellarListing = stellarListing?.source === 'stellar';

  useEffect(() => {
    if (isExternalStellarListing) {
      setProperty(stellarListing);
      return;
    }

    if (state?.properties) {
      const foundProperty = state.properties.find(
        (p) => String(p.propertyId) === String(id)
      );
      setProperty(foundProperty);
      if (foundProperty?.listingStatus) {
        setStatus(foundProperty.listingStatus);
        setSalePrice(foundProperty.salePrice || '');
        setSaleDate(foundProperty.saleDate || '');
      }
    }
  }, [id, state?.properties, isExternalStellarListing, stellarListing]);

  if (!property) return <LoadingSpinner message="Loading property…" />;

  // Identify listing agent by public user_id included in listings payload
  const listAgent = state.users?.find(
    (user) => String(user.userId) === String(property.listingUserId)
  );
  console.log('state', state);
  console.log('List Agent:', listAgent);
  
  const imageUrl = property.imageUrl || 'https://via.placeholder.com/300';

  // Only the creator of the listing may edit/delete it
  const currentUserId = state?.user?.userId ?? state?.user?.id;
  const canEditListing =
    !isExternalStellarListing &&
    currentUserId != null &&
    property?.listingUserId != null &&
    String(currentUserId) === String(property.listingUserId);

  const renderField = (label, value, suffix = '') => {
    if (value !== undefined && value !== null && value !== '') {
      return (
        <p>
          <strong>{label}:</strong> {value}
          {suffix}
        </p>
      );
    }
    return null;
  };

  const handleStatusUpdate = async () => {
    if (!canEditListing) {
      setSnackbar({ show: true, message: '❌ You do not have permission to modify this listing', type: 'danger' });
      return;
    }
    try {
      const payload = {
        property_id: property.propertyId,
        listing_status: status,
        ...(status === 'Sold' && { sale_price: salePrice, sale_date: saleDate }),
      };


      const response = await apiFetch('updateListingStatus', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (response.success) {
        setSnackbar({ show: true, message: '✅ Listing status updated', type: 'success' });
        setProperty((prev) => ({
          ...prev,
          listingStatus: status,
          ...(status === 'Sold' && { salePrice, saleDate }),
        }));

        // Update global state
        dispatch({
          type: 'UPDATE_PROPERTY',
          payload: {
            ...property,
            listingStatus: status,
            ...(status === 'Sold' && { salePrice, saleDate }),
          },
        });

        setIsModalOpen(false);
      } else {
        setSnackbar({ show: true, message: `❌ Failed to update: ${response.message}`, type: 'danger' });
      }
    } catch (err) {
      console.error('Error updating listing status:', err);
      setSnackbar({ show: true, message: '❌ Error updating listing status', type: 'danger' });
    }
  };

  return (
    <div className="container mt-4">
      <Snackbar
        show={snackbar.show}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar((s) => ({ ...s, show: false }))}
      />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <button className="btn btn-light btn-sm" onClick={() => navigate(-1)}>
          &larr; Back
        </button>
        {canEditListing && (
          <div>
            <Link
              to={`/edit/${property.propertyId}`}
              className="btn btn-primary btn-sm me-2"
            >
              Edit Property
            </Link>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setIsModalOpen(true)}
            >
              Edit Status
            </button>
            {property.listingStatus && (
              <button
                className="btn btn-danger btn-sm ms-2"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete Listing
              </button>
            )}
          </div>
        )}
      </div>

      <h1>Property Details</h1>

      <div className="card shadow-sm">
        <img
          src={imageUrl}
          className="card-img-top"
          alt="Property"
          style={{ height: '300px', objectFit: 'cover' }}
        />

        <div className="card-body">
          {renderField(
            'Address',
            `${property.streetAddress}, ${property.city}, ${property.state} ${property.zipCode}`
          )}
          {renderField('Status', property.listingStatus)}
          {property.listingStatus === 'Sold' &&
            renderField('Sale Price', property.salePrice)}
          {property.listingStatus === 'Sold' &&
            renderField('Sale Date', property.saleDate)}

          <div className="row mt-3">
            <div className="col-md-6">
              {renderField('List Price', property.listPrice)}
              {renderField('MLS #', property.mls_id)}
              {renderField('Bedrooms', property.bedrooms)}
              {renderField('Bathrooms', property.bathrooms)}
              {renderField('Acreage', property.acreage, ' acres')}
              {renderField('Property Type', property.propertySubtype || property.propertyType)}
            </div>
            <div className="col-md-6">
              {renderField('Living SqFt', property.livingSqFt, ' sqft')}
              {renderField('Total SqFt', property.totalSqFt, ' sqft')}
              {renderField('Year Built', property.yearBuilt)}
              {renderField('Zoning', property.zoning)}
              {renderField('Parcel Number', property.parcelNumber)}
              {renderField('Seller Fee', property.sellerFee, '%')}

              {!isExternalStellarListing && (
                <p>
                  <strong>List Agent:</strong>
                  {listAgent ? (
                    <Link to={`/profile/${listAgent.userId}`} className="ms-2">
                      {listAgent.name}
                    </Link>
                  ) : (
                    <span className="text-muted ms-2">Unknown</span>
                  )}
                </p>
              )}
              {isExternalStellarListing && (
                <p>
                  <strong>Data Source:</strong>
                  <span className="text-muted ms-2">Stellar MLS</span>
                </p>
              )}
            </div>
          </div>

          <hr />

          <div className="row">
            <div className="col-md-6">
              {renderField('Buyer Fee', property.buyerFee, '%')}
            </div>
            <div className="col-md-6">
              <div>More Info</div>
              <a
                href={property.listingLink || 'https://google.com'}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div>
                  <strong>Click Here</strong>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Status Modal */}
      {isModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Listing Status</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setIsModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body">
                <label className="form-label">Select Status</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="Active">Active</option>
                  <option value="Pending">Pending</option>
                  <option value="Sold">Sold</option>
                  <option value="Withdrawn">Withdrawn</option>
                  <option value="Canceled">Canceled</option>
                  <option value="Expired">Expired</option>
                </select>

                {status === 'Sold' && (
                  <>
                    <div className="mt-3">
                      <label className="form-label">Sale Price</label>
                      <input
                        type="number"
                        className="form-control"
                        value={salePrice}
                        onChange={(e) => setSalePrice(e.target.value)}
                        placeholder="Enter sale price"
                      />
                    </div>
                    <div className="mt-3">
                      <label className="form-label">Sale Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={saleDate}
                        onChange={(e) => setSaleDate(e.target.value)}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsModalOpen(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleStatusUpdate}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Listing Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Listing</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Are you sure you want to delete the listing for
            {' '}<strong>{property.streetAddress}</strong>? This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={deleting}
            onClick={async () => {
              if (!canEditListing) {
                setSnackbar({ show: true, message: '❌ You do not have permission to delete this listing', type: 'danger' });
                return;
              }
              setDeleting(true);
              try {
                await apiFetch('deleteListing', {
                  method: 'POST',
                  body: JSON.stringify({ property_id: property.propertyId })
                });

                // Optimistically update local property state (clear listing-specific fields)
                const cleared = {
                  ...property,
                  listingStatus: null,
                  listPrice: null,
                  listingLink: null,
                  listingDescription: null,
                  imageUrl: null,
                  buyerFee: null,
                  expirationDate: null,
                  listingUserId: null,
                };
                setProperty(cleared);

                // Update global properties collection
                dispatch({ type: 'UPDATE_PROPERTY', payload: cleared });

                // Update global listings collection if present
                if (Array.isArray(state.listings)) {
                  const updatedListings = state.listings.filter(l => String(l.propertyId) !== String(property.propertyId));
                  dispatch({ type: 'SET_LISTINGS', payload: updatedListings });
                }

                setSnackbar({ show: true, message: '✅ Listing deleted', type: 'success' });
                setShowDeleteModal(false);
              } catch (err) {
                console.error('Failed to delete listing:', err);
                setSnackbar({ show: true, message: `❌ Failed to delete: ${err.message}`, type: 'danger' });
              } finally {
                setDeleting(false);
              }
            }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default PropertyDetail;
